import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users as UsersIcon, Loader2, Pencil, KeyRound, Save, Trash2, MapPin, ExternalLink, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { RoleBadge, roleBadgeClass } from '../../components/common/RoleBadge';
import { ADMIN_NAV } from '../../constants/nav';
import { adminListUsers, adminUpdateUser, adminResetUserPassword, adminDeleteUser, adminLocationStats, LocationStats } from '../../features/users/api/users';
import type { User } from '../../types';
import { USERS } from '../../constants/testIds';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { DateRangeField } from '../../components/common/DatePickerFields';

const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';
const inputClass = 'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const ALL_ROLES = ['member', 'vip', 'webinar', 'workshop', 'mentor', 'sales', 'admin'];
const ROLE_LABELS: Record<string, string> = {
  member: 'Member',
  vip: 'VIP',
  webinar: 'Webinar Member',
  workshop: 'Workshop Member',
  mentor: 'Mentor',
  sales: 'Sales',
  admin: 'Admin',
};

function locationText(u: User): string | null {
  const l = u.location;
  if (!l) return null;
  const parts = [l.city, l.region, l.country].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function mapsUrl(u: User): string | null {
  const l = u.location;
  if (!l || l.lat == null || l.lng == null) return null;
  return `https://www.google.com/maps/search/?api=1&query=${l.lat},${l.lng}`;
}

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<LocationStats | null>(null);
  const [confirmDel, setConfirmDel] = useState<User | null>(null);
  const [delLoading, setDelLoading] = useState(false);

  const [editUser, setEditUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [status, setStatus] = useState('active');
  const [isVip, setIsVip] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const [resetUser, setResetUser] = useState<User | null>(null);
  const [newPw, setNewPw] = useState('');
  const [savingReset, setSavingReset] = useState(false);

  const load = () => {
    setLoading(true);
    adminListUsers().then(setUsers).finally(() => setLoading(false));
    adminLocationStats().then(setStats).catch(() => {});
  };
  useEffect(load, []);

  const openEdit = (u: User) => { setEditUser(u); setRoles([...u.roles]); setStatus(u.status); setIsVip(u.is_vip); };
  const toggleRole = (r: string) => setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  const saveEdit = async () => {
    if (!editUser) return;
    setSavingEdit(true);
    try {
      await adminUpdateUser(editUser.id, { roles, status, is_vip: isVip });
      toast.success('User diperbarui'); setEditUser(null); load();
    } catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSavingEdit(false); }
  };

  const openReset = (u: User) => { setResetUser(u); setNewPw(''); };
  const saveReset = async () => {
    if (!resetUser) return;
    if (newPw.length < 6) { toast.error('Password minimal 6 karakter'); return; }
    setSavingReset(true);
    try {
      await adminResetUserPassword(resetUser.id, newPw);
      toast.success(`Password ${resetUser.email} direset`); setResetUser(null);
    } catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSavingReset(false); }
  };

  const del = (u: User) => setConfirmDel(u);
  const doDelete = async () => {
    if (!confirmDel) return;
    setDelLoading(true);
    try { await adminDeleteUser(confirmDel.id); toast.success('User dihapus'); load(); }
    catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setDelLoading(false); setConfirmDel(null); }
  };

  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<'all' | 'today' | '7' | '30' | 'custom'>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const joinedText = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const withinPeriod = (iso?: string) => {
    if (period === 'all') return true;
    if (!iso) return false;
    if (period === 'custom') {
      const d = new Date(iso).getTime();
      if (fromDate && d < new Date(fromDate + 'T00:00:00').getTime()) return false;
      if (toDate && d > new Date(toDate + 'T23:59:59').getTime()) return false;
      return true;
    }
    if (period === 'today') return new Date(iso).toDateString() === new Date().toDateString();
    const days = Number(period);
    const diff = (Date.now() - new Date(iso).getTime()) / 86400000;
    return diff <= days;
  };
  const q = search.trim().toLowerCase();
  const filtered = users.filter((u) => {
    const matchQ = !q || u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || (u.phone || '').includes(q);
    return matchQ && withinPeriod(u.created_at);
  });

  const pBtn = (p: typeof period, label: string) => (
    <button data-testid={`users-filter-${p}`} onClick={() => setPeriod(p)} className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${period === p ? 'bg-cyan-400 text-[#050914] font-medium' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>{label}</button>
  );

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Users">
      <div className="max-w-5xl" data-testid={USERS.adminPage}>
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><UsersIcon className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /></div>
          <div><p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Manajemen</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Users ({users.length})</h2></div>
        </div>

        {stats && stats.total_with_location > 0 && (
          <div data-testid="users-location-stats" className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
              <p className="text-sm font-medium text-white">Sebaran Lokasi Member</p>
              <span className="text-xs text-white/40">({stats.total_with_location} punya lokasi)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.by_region.slice(0, 12).map((r) => (
                <span key={r.name} className="text-xs text-white/70 bg-white/[0.03] border border-white/10 rounded-full px-3 py-1">
                  {r.name} <span className="text-cyan-400 font-medium">{r.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4" data-testid="users-filter-bar">
          <input
            data-testid="users-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama / email / no. HP..."
            className="flex-1 bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none text-sm"
          />
          <div className="flex gap-1 bg-white/[0.03] border border-white/10 rounded-lg p-1 shrink-0 flex-wrap">
            {pBtn('today', 'Hari ini')}{pBtn('7', '7 hari')}{pBtn('30', '30 hari')}{pBtn('custom', 'Custom')}{pBtn('all', 'Semua')}
          </div>
        </div>
        {period === 'custom' && (
          <div className="flex flex-wrap items-center gap-3 mb-4 -mt-1" data-testid="users-custom-range">
            <div className="w-full sm:w-80">
              <DateRangeField
                testId="users-date-range"
                from={fromDate}
                to={toDate}
                onChange={({ from, to }) => { setFromDate(from); setToDate(to); }}
                placeholder="Pilih rentang tanggal bergabung"
              />
            </div>
          </div>
        )}

        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div> : (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
            <p className="text-xs text-white/40 px-4 pt-3 pb-2">Menampilkan {filtered.length} dari {users.length} user</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]" data-testid="users-table">
                <thead>
                  <tr className="text-left text-white/40 text-[11px] uppercase tracking-wider border-b border-white/10">
                    <th className="px-4 py-3 font-medium">No</th>
                    <th className="px-4 py-3 font-medium">Nama</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Telp</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap">Bergabung</th>
                    <th className="px-4 py-3 font-medium text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u, i) => (
                    <tr key={u.id} data-testid={USERS.row} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors align-top">
                      <td className="px-4 py-3 text-white/40">{i + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium">{u.full_name}</span>
                          {u.is_vip && <span className="text-[10px] uppercase text-amber-300 bg-amber-400/10 border border-amber-400/20 px-1.5 py-0.5 rounded-full">VIP</span>}
                        </div>
                        {locationText(u) && (
                          <span className="text-xs text-white/40 flex items-center gap-1 mt-0.5 flex-wrap" data-testid="user-location">
                            <MapPin className="w-3 h-3 text-cyan-400/70" strokeWidth={1.5} />
                            {locationText(u)}
                            <span className="text-[9px] uppercase tracking-wider text-white/30 border border-white/10 rounded px-1 ml-1">{u.location?.source}</span>
                            {mapsUrl(u) && (
                              <a href={mapsUrl(u)!} target="_blank" rel="noreferrer" data-testid="user-location-maps" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300 ml-1">
                                <ExternalLink className="w-3 h-3" strokeWidth={1.5} /> Maps
                              </a>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/60 break-all">{u.email}</td>
                      <td className="px-4 py-3 text-white/60 whitespace-nowrap">{u.phone || '—'}</td>
                      <td className="px-4 py-3"><div className="flex gap-1 flex-wrap max-w-[160px]">{u.roles.map((r) => <RoleBadge key={r} role={r} />)}</div></td>
                      <td className="px-4 py-3">
                        {u.status === 'active'
                          ? <span className="text-[10px] uppercase text-emerald-300 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full">active</span>
                          : <span className="text-[10px] uppercase text-red-300 bg-red-400/10 border border-red-400/20 px-2 py-0.5 rounded-full">{u.status}</span>}
                      </td>
                      <td className="px-4 py-3 text-white/50 whitespace-nowrap" data-testid="user-joined">
                        {joinedText(u.created_at)}
                        {u.referral_source && <span className="block text-[11px] text-cyan-400/70 capitalize">via {u.referral_source}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <button data-testid={`user-vip-link-${u.id}`} onClick={() => navigate(`/admin/vip?user=${encodeURIComponent(u.email)}`)} title="Kelola VIP member ini" className={`p-2 rounded-lg hover:bg-white/5 ${u.is_vip ? 'text-amber-300 hover:text-amber-200' : 'text-white/60 hover:text-amber-300'}`}><Crown className="w-4 h-4" strokeWidth={1.5} /></button>
                          <button data-testid={USERS.editBtn} onClick={() => openEdit(u)} className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                          <button data-testid={USERS.resetBtn} onClick={() => openReset(u)} className="p-2 rounded-lg text-white/60 hover:text-amber-400 hover:bg-white/5"><KeyRound className="w-4 h-4" strokeWidth={1.5} /></button>
                          <button data-testid="user-delete-button" onClick={() => del(u)} className="p-2 rounded-lg text-white/60 hover:text-red-400 hover:bg-white/5"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Edit User</DialogTitle><DialogDescription className="text-white/50">{editUser?.email}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Roles</label>
              <div className="flex flex-wrap gap-2">
                {ALL_ROLES.map((r) => (
                  <button key={r} data-testid={`role-toggle-${r}`} onClick={() => toggleRole(r)} className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${roles.includes(r) ? `${roleBadgeClass(r)} font-medium` : 'text-white/60 border-white/10 hover:border-white/30'}`}>{ROLE_LABELS[r] || r}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select data-testid="user-status-select" className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer"><input type="checkbox" data-testid="user-vip-check" checked={isVip} onChange={(e) => setIsVip(e.target.checked)} className="accent-cyan-400 w-4 h-4" /> Tandai sebagai VIP</label>
            {editUser && (
              <button data-testid="user-edit-vip-link" onClick={() => navigate(`/admin/vip?user=${encodeURIComponent(editUser.email)}`)} className="flex items-center gap-2 text-xs text-amber-300 hover:text-amber-200 transition-colors">
                <Crown className="w-3.5 h-3.5" strokeWidth={1.5} /> Buka pengajuan VIP member ini di VIP Management →
              </button>
            )}
          </div>
          <DialogFooter>
            <button data-testid={USERS.saveBtn} onClick={saveEdit} disabled={savingEdit || roles.length === 0} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">{savingEdit ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Save className="w-4 h-4" strokeWidth={2} />} Simpan</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription className="text-white/50">Set password baru untuk {resetUser?.email}</DialogDescription></DialogHeader>
          <div><label className={labelClass}>Password Baru</label><input data-testid="user-reset-input" type="password" className={inputClass} value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Minimal 6 karakter" /></div>
          <DialogFooter>
            <button data-testid={USERS.resetSaveBtn} onClick={saveReset} disabled={savingReset || newPw.length < 6} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">{savingReset ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <KeyRound className="w-4 h-4" strokeWidth={2} />} Reset</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!confirmDel}
        title="Hapus user?"
        description={confirmDel ? `${confirmDel.email} akan dihapus permanen.` : ''}
        loading={delLoading}
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(null)}
      />
    </DashboardLayout>
  );
}
